import { Attrs, EventListeners, MinzeElement, Reactive } from 'minze'
import { Font } from '../../constants'
import { Callback } from '../../modules/callback'
import { Locale } from '../../modules/locale'

Locale.getInstance().registerKeySet('fr', 'contributorsSelector', {
  addContributorButton: 'Ajouter un contributeur',
  contributorTypeLabel: 'Type de contributeur*',
  contributionTypeLabel: 'Type de contribution*',
  delete: 'Supprimer',
  contributorPlaceholder: 'Contributeur',
  contributorTypesLabels: 'Non spécifié,Personne,Organisation',
  contributionTypesLabels:
    'Non spécifié,Artiste,Auteur,Équipe créative,Personnel technique,Mise en scène,Direction artistique,Producteur,Organisateur,Promoteur,Sponsor,Autre',
})

Locale.getInstance().registerKeySet('en', 'contributorsSelector', {
  addContributorButton: 'Add a contributor',
  contributorTypeLabel: 'Type of contributor*',
  contributionTypeLabel: 'Type of contribution*',
  delete: 'Delete',
  contributorPlaceholder: 'Contributor',
  contributorTypesLabels: 'Not specified,Person,Organization',
  contributionTypesLabels:
    'Not specified,Artist,Author,Creative team,Technical staff,Stage direction,Artistic direction,Producer,Organiser,Promoter,Sponsor,Other',
})

interface IContributor {
  contributorName: string
  contributorType: string
  contributionType: string
  contributorData?: any
}

const createContributorInitialState: IContributor = {
  contributorName: '',
  contributorType: '',
  contributionType: '',
}

export interface LvcContributorsSelector {
  contributors: IContributor[]
  errors: { index: number; key: string; message: string }[]
  defaultValue: any
}

const CONTRIBUTOR_TYPES_SELECT_VALUES = ['', 'Person', 'Organization'].join(',')
const CONTRIBUTION_TYPES_SELECT_VALUES = [
  '',
  'Artiste',
  'Auteur',
  'Équipe de création',
  'Personnel technique',
  'Mise en scène',
  'Direction artistique',
  'Producteur',
  'Organisateur',
  'Promoteur',
  'Commanditaire',
  'Autre',
].join(',')

export class LvcContributorsSelector extends MinzeElement {
  attrs: Attrs = [['default-value', '']]
  reactive: Reactive = [
    ['contributors', []],
    ['errors', []],
  ]

  static observedAttributes = ['default-value']

  private onChangeCb: string = ''
  private onChangeNameCb: string = ''
  contributorKeys = ['contributorName', 'contributorType', 'contributionType']
  inputIds = ['contributorType', 'contributionType']

  onAttributeChange(
    name?: string | undefined,
    oldValue?: string | null | undefined,
    newValue?: string | null | undefined
  ): void | Promise<void> {
    if (name === 'default-value') {
      this.updateDefaultValue()
    }
  }

  async updateDefaultValue() {
    if (this.defaultValue && this.defaultValue !== true) {
      this.contributors = JSON.parse(decodeURIComponent(this.defaultValue))
      this.rerender()
    }
  }

  onReady(): Promise<void> | void {
    this.onChangeCb = Callback.attach(this.onChange.bind(this))
    this.onChangeNameCb = Callback.attach(this.onChangeName.bind(this))
    // if there are no contributors, we add an empty one by default
    if (this.contributors.length === 0) {
      this.contributors = [{ ...createContributorInitialState }]
    } else {
      this.validate()
    }
    this.rerender()
  }

  setError(index: number, key: string, message: string) {
    const errorIndex = this.errors.findIndex(error => {
      return error.index === index && error.key === key
    })
    if (errorIndex > -1) this.errors[errorIndex] = { index, key, message }
    else this.errors.push({ index, key, message })
  }

  onChangeName(data: { element: HTMLElement; value: unknown[] }) {
    const index = data.element?.getAttribute('data-index')

    if (index) {
      // if value is a string, it means that the user has created a new contributor
      if (typeof data.value === 'string') {
        this.contributors[parseInt(index)].contributorName = data.value
      } else {
        this.contributors[parseInt(index)] = {
          ...this.contributors[parseInt(index)],
          contributorName: (data.value as any)?.[0]?.contributorName || '',
          contributorType: (data.value as any)?.[0]?.contributorType || '',
        }
      }
    }
    this.cast('contributors-selector:change', {
      contributors: this.contributors,
    })

    this.rerender()
  }

  onChange(event: Event) {
    const target = event.target as HTMLInputElement
    if (target && target.value) {
      const shadowRoot: any = target.getRootNode()
      const index = shadowRoot.host.getAttribute('data-index')
      if (index) this.setError(parseInt(index), target.name, 'false')
    }
    const getContributorValues = (index: number): IContributor => {
      const contributor: IContributor = { ...this.contributors[index] }
      return this.inputIds.reduce((acc, id) => {
        const element = this.select(`[name="${id}"][data-index="${index}"]`) as HTMLInputElement
        contributor[id as keyof IContributor] = element?.value || ''
        return contributor
      }, {} as IContributor)
    }
    this.contributors.forEach((contributor, index) => {
      this.contributors[index] = getContributorValues(index)
    })
    this.cast('contributors-selector:change', {
      contributors: this.contributors,
    })
    this.rerender()
  }

  addContributor() {
    let isValid = true

    this.contributors.forEach((contributor, index) => {
      this.contributorKeys.forEach(id => {
        if (!contributor[id as keyof IContributor]) {
          this.setError(index, id, Locale.getKey('general.required'))
          isValid = false
        }
      })
    })
    if (isValid) this.contributors = [...this.contributors, { ...createContributorInitialState }]
    this.rerender()
  }

  removeContributor(event: Event) {
    const target = (event?.target as HTMLElement)?.closest('.remove-contributor')
    if (!target) return
    const index = target.getAttribute('data-index')
    if (!index) return
    const updatedArray = [...this.contributors]
    updatedArray.splice(parseInt(index), 1)
    this.contributors = [...updatedArray]
    this.cast('contributors-selector:change', {
      contributors: this.contributors,
    })

    this.rerender()
  }

  validate() {
    // if one of the fields is empty in a contributor, return a general error on that field
    this.contributors.forEach((contributor, index) => {
      this.contributorKeys.forEach(id => {
        if (!contributor[id as keyof IContributor]) {
          this.setError(index, id, Locale.getKey('general.required'))
        }
      })
    })
  }

  html = () => `
    <lvc-box mb="40px">
    <lvc-box display="flex" flexDirection="column" rowGap="24px">
      ${this.contributors
        .map(
          (contributor, index) => `
        <lvc-box>
          <lvc-text font-preset="medium">
            ${contributor.contributorName || `${Locale.getKey('contributorsSelector.contributorPlaceholder')} ${index + 1}`}
          </lvc-text>
          <lvc-box
            rowGap="16px"
            display="flex"
            flexDirection="column"
            background="white"
            padding="24px"
            borderRadius="8px"
            border="1px solid #1E007D4D"
            mt="8px"
          >
            <lvc-contributors-search-field
              data-index="${index}"
              on-change="${this.onChangeNameCb}"
              default-value="${encodeURIComponent(JSON.stringify(this.contributors[index])) || ''}"
              error="${
                this.errors.find(error => {
                  return error.index === index && error.key === 'contributorName'
                })?.message || 'false'
              }"
            ></lvc-contributors-search-field>
            <lvc-box display="flex" columnGap="16px">
              <lvc-select
                name="contributorType"
                placeholder=""
                id="contributorType"
                default-value="${this.contributors[index].contributorType}"
                label="${Locale.getKey('contributorsSelector.contributorTypeLabel')}"
                secondary-style="true"
                selectOptions="${CONTRIBUTOR_TYPES_SELECT_VALUES}"
                selectOptionLabels="${Locale.getKey('contributorsSelector.contributorTypesLabels')}"
                onChange="${this.onChangeCb}"
                data-index="${index}"
                error="${
                  this.errors.find(error => {
                    return error.index === index && error.key === 'contributorType'
                  })?.message || 'false'
                }"
              ></lvc-select>
              <lvc-select
                name="contributionType"
                id="contributionType"
                placeholder=""
                default-value="${this.contributors[index].contributionType}"
                label="${Locale.getKey('contributorsSelector.contributionTypeLabel')}"
                secondary-style="true"
                selectOptions="${CONTRIBUTION_TYPES_SELECT_VALUES}"
                selectOptionLabels="${Locale.getKey('contributorsSelector.contributionTypesLabels')}"
                onChange="${this.onChangeCb}"
                data-index="${index}"
                error="${
                  this.errors.find(error => {
                    return error.index === index && error.key === 'contributionType'
                  })?.message || 'false'
                }"
              ></lvc-select>
            </lvc-box>
          </lvc-box>
          <lvc-box textAlign="right">
            <button class="remove-contributor" data-index="${index}">
              <lvc-text text-decoration="underline" font-weight="${Font.weight.medium}" font-size="12px">${Locale.getKey('contributorsSelector.delete')}</lvc-text>
            </button>
          </lvc-box>
        </lvc-box>
      `
        )
        .join('')}
    </lvc-box>
    <lvc-box mt="24px" width="100%">
      <lvc-button id="add-contributor" border="1px solid #1E007D" background="transparent" color="#1E007D" width="100%">
        <lvc-box display="flex" justifyContent="center" alignItems="center" columnGap="8px">
          <lvc-add-icon data-color="#1E007D"></lvc-add-icon>
          ${Locale.getKey('contributorsSelector.addContributorButton')}
        </lvc-box>
      </lvc-button>
    </lvc-box>
  `

  css = () => `
    :host {
    }
    lvc-add-icon {
      width: 20px;
      height: 20px;
    }
    .remove-contributor {
      background: transparent;
      border: none;
      padding: 8px 0;
      cursor: pointer;
    }
  `

  eventListeners: EventListeners = [
    ['#add-contributor', 'click', this.addContributor.bind(this)],
    ['.remove-contributor', 'click', this.removeContributor.bind(this)],
  ]
}
